export interface FullyVisibleResult {
	isBottomVisible: boolean;
	isFullyVisible: boolean;
	isLeftVisible: boolean;
	isRightVisible: boolean;
	isTopVisible: boolean;
}

export function getCaretPosition(element: HTMLElement): number {
	if (element.contentEditable) {
		const selection = window.getSelection();

		if (
			!selection
			|| selection.rangeCount === 0
		) {
			/**
			 * No selection available
			 */
			return 0;
		}

		const range = selection.getRangeAt(0);
		const preCaretRange = range.cloneRange();
		preCaretRange.selectNodeContents(element);
		preCaretRange.setEnd(
			range.endContainer,
			range.endOffset,
		);
		const childNodes = Array.from(preCaretRange.cloneContents().childNodes);
		let cursorPosition = 0;

		// eslint-disable-next-line no-restricted-syntax
		for (const node of childNodes) {
			if (node === range.endContainer) {
				cursorPosition += range.endOffset;
				break;
			}

			if (node.nodeType === Node.TEXT_NODE) {
				cursorPosition += (node as Text).length;
			} else if (node.textContent) {
				cursorPosition += node.textContent.length;
				/**
				 * Adjust cursor position for block elements or line breaks
				 */
				if (
					(node as Element).tagName === 'BR'
					|| window.getComputedStyle(node as Element).display === 'block'
				) {
					/**
					 * Optionally, adjust based on the definition of newline length
					 */
					cursorPosition += 1;
				}
			}
		}

		const amountOfNewLines = element.innerText.match(/\n/g)?.length || 0;

		if (
			amountOfNewLines > 0
			&& element.textContent
			&& element.innerText.replace(
				/\n/g,
				'',
			).length === element.textContent.length - 1
			&& element.textContent.at(-1) === ' '
		) {
			cursorPosition += 1;
		}

		return cursorPosition;
	}

	return 0;
}

export function isFullyVisible(element: HTMLElement): FullyVisibleResult {
	const result: FullyVisibleResult = {
		isBottomVisible: true,
		isFullyVisible: true,
		isLeftVisible: true,
		isRightVisible: true,
		isTopVisible: true,
	};

	if (!element) {
		return {
			isBottomVisible: false,
			isFullyVisible: false,
			isLeftVisible: false,
			isRightVisible: false,
			isTopVisible: false,
		};
	}

	const elementRect = element.getBoundingClientRect();
	let currentElement = element;

	while (
		currentElement
		&& currentElement.parentElement
	) {
		const { parentElement } = currentElement;
		const parentRect = parentElement.getBoundingClientRect();

		const isScrollNotMatching = (
			element.scrollWidth > parentElement.scrollWidth
			|| element.scrollHeight > parentElement.scrollHeight
		);
		const isTopNotVisible = elementRect.top < parentRect.top;
		const isLeftNotVisible = elementRect.left < parentRect.left;
		const isRightNotVisible = Math.floor(parentRect.left + parentElement.scrollWidth) < Math.floor(elementRect.left + element.scrollWidth);
		const isBottomNotVisible = Math.floor(parentRect.top + parentElement.scrollHeight) < Math.floor(elementRect.top + element.scrollHeight);

		if (
			isScrollNotMatching
			|| isTopNotVisible
			|| isLeftNotVisible
			|| isRightNotVisible
			|| isBottomNotVisible
		) {
			result.isFullyVisible = false;
		}

		if (isTopNotVisible) {
			result.isTopVisible = false;
		}
		if (isLeftNotVisible) {
			result.isLeftVisible = false;
		}
		if (isRightNotVisible) {
			result.isRightVisible = false;
		}
		if (isBottomNotVisible) {
			result.isBottomVisible = false;
		}

		currentElement = currentElement.parentElement;
	}

	return result;
}

export function isOrHasParent(
	element: HTMLElement,
	parent: HTMLElement,
): boolean {
	if (element === parent) {
		return true;
	}

	if (!element.parentElement) {
		return false;
	}

	return isOrHasParent(
		element.parentElement,
		parent,
	);
}

export function isRectScrollable(
	element: HTMLElement,
	parentToStop?: HTMLElement,
): boolean {
	const isElementScrollable = !(
		element.scrollWidth <= element.clientWidth
		&& element.scrollHeight <= element.clientHeight
	);

	if (
		isElementScrollable
		|| !element.parentElement
		|| element.parentElement.id === 'webapp'
		|| parentToStop?.isSameNode(element.parentElement)
	) {
		return isElementScrollable;
	}

	return isRectScrollable(
		element.parentElement,
		parentToStop,
	);
}

export function onElementChildrenChange(
	element: Element,
	callback: () => void,
): () => void {
	if (!element) {
		return () => undefined;
	}

	const observersDisconnects: (() => void)[] = [];
	const onImageLoad = (
		target: HTMLElement,
		onLoad: () => void,
	) => {
		const styles = getComputedStyle(target);

		if (target instanceof HTMLImageElement) {
			if (
				target.complete
				&& target.naturalWidth !== 0
			) {
				onLoad();
			} else {
				target.addEventListener(
					'load',
					() => onLoad(),
				);
			}
		} else if (styles.backgroundImage !== 'none') {
			const image = new Image();
			image.addEventListener(
				'load',
				() => onLoad(),
			);

			if (styles.backgroundImage.startsWith('url(')) {
				image.src = styles.backgroundImage
					.slice(
						4,
						-1,
					)
					.replace(
						/("|')/g,
						'',
					);
			} else {
				image.src = styles.backgroundImage;
			}
		}
	};
	const checkForImagesLoad = (
		target: HTMLElement,
		onLoad: () => void,
	) => {
		onImageLoad(
			target,
			onLoad,
		);

		if (target.children.length > 0) {
			Array.prototype.forEach.call(
				target.children,
				(child: HTMLElement) => {
					checkForImagesLoad(
						child as HTMLElement,
						onLoad,
					);
				},
			);
		}
	};
	const mutationObserver = new MutationObserver((mutationsList) => {
		callback();

		// eslint-disable-next-line no-restricted-syntax
		for (const mutation of mutationsList) {
			if (mutation.type === 'childList') {
				const target = mutation.target as HTMLElement;

				Array.prototype.forEach.call(
					target.children,
					(child: HTMLElement) => {
						onImageLoad(
							child,
							callback,
						);

						if (child.children.length > 0) {
							observersDisconnects.push(
								onElementChildrenChange(
									child,
									callback,
								),
							);
						}
					},
				);
			} else if (mutation.type === 'attributes') {
				if (mutation.target instanceof HTMLElement) {
					onImageLoad(
						mutation.target,
						callback,
					);
				}
			}
		}
	});
	mutationObserver.observe(
		element,
		{
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		},
	);
	const resizeObserver = new ResizeObserver(() => callback());
	resizeObserver.observe(element);
	observersDisconnects.push(() => resizeObserver.disconnect());
	observersDisconnects.push(() => mutationObserver.disconnect());
	checkForImagesLoad(
		element as HTMLElement,
		callback,
	);

	return () => {
		// eslint-disable-next-line no-restricted-syntax
		for (const observerDisconnect of observersDisconnects) {
			observerDisconnect();
		}
	};
}

export function setCaretPosition(
	element: HTMLElement,
	position: number,
	wasContentEditable?: boolean,
): void {
	if (!element) {
		return;
	}

	if (
		!element.contentEditable
		&& !wasContentEditable
	) {
		if (element instanceof HTMLInputElement) {
			element.setSelectionRange(
				position,
				position,
			);
		} else {
			document.getSelection()?.collapse(
				element,
				position,
			);
		}
	} else {
		let currentNodePosition = 0;
		const nodes = Array.from(element.childNodes);
		let found = false;

		// eslint-disable-next-line no-restricted-syntax
		for (const node of nodes) {
			const length = (
				node.nodeType === Node.TEXT_NODE
					? (node.nodeValue as string).length
					: (node.textContent as string).length
			);

			if ((currentNodePosition + length) >= position) {
				/**
				 * Found the node where the cursor should be placed
				 */
				const offset = position - currentNodePosition;

				if (node.nodeType === Node.TEXT_NODE) {
					const range = document.createRange();
					const selection = window.getSelection();
					range.setStart(
						node,
						offset,
					);
					range.collapse(true);
					selection?.removeAllRanges();
					selection?.addRange(range);
				} else {
					/**
					 * If it's not a text node, call this function recursively on child nodes
					 */
					setCaretPosition(
						node as HTMLElement,
						offset,
						true,
					);
				}
				found = true;
				break;
			} else {
				currentNodePosition += length;
			}
		}

		/**
		 * If we didn't find a suitable node (for instance, if the position is at the very end), place the cursor at the end
		 */
		if (
			!found
			&& nodes.length > 0
		) {
			const lastNode = nodes.at(-1) as HTMLElement;
			setCaretPosition(
				lastNode,
				(
					lastNode.nodeType === Node.TEXT_NODE
						? (lastNode.nodeValue as string).length
						: (lastNode.textContent as string).length
				),
				true,
			);
		}
	}
}
